import { normalizeApproximateSettings, type ApproximateCostPlanSettings } from './approximateCostPlan';
import type { DavisBaconConstructionType, EstimateComparableReview, EstimateWorkItem } from './constructionPriceEstimatorFunctions';

export interface ConstructionTimingRequirement {
  id: string;
  kind: 'overall_performance_period' | 'phase_duration' | 'milestone_deadline' | 'work_access_restriction' | 'notice' | 'review' | 'delivery' | 'qualitative_dependency';
  strength: 'required' | 'planning_target' | 'unspecified';
  label: string;
  value: string | null;
  value_max?: string | null;
  unit: 'calendar_days' | 'workdays' | 'unspecified_days' | 'weeks' | 'months' | 'hours' | 'years' | null;
  trigger?: string | null;
  phase?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  conditions: string[];
  source_id: string;
  source_locator: string;
  evidence_quote: string;
  source_kind?: 'uploaded_sow' | 'statement_of_work' | 'project_brief' | null;
  document_name?: string | null;
  discrepancy?: string | null;
  owner_override?: { description: string } | null;
}

export interface EstimateDraft {
  timing_requirements?: ConstructionTimingRequirement[];
  approximate_cost_plan_settings?: ApproximateCostPlanSettings | null;
  draft_revision: number;
  content_hash?: string;
  project_location: Record<string, string | null | undefined>;
  estimate_date: string;
  construction_type: string;
  price_search_terms?: string[];
  davis_bacon_construction_type?: DavisBaconConstructionType | null;
  size_drivers: Array<Record<string, unknown>>;
  work_items: EstimateWorkItem[];
  project_assumptions: string[];
  exclusions: string[];
  adjustments: Array<Record<string, unknown>>;
  required_acknowledgements: string[];
  comparable_reviews?: EstimateComparableReview[];
}

/** Retain only owner-visible timing evidence; never copy private source paths or model metadata. */
export const normalizeTimingRequirements = (value: unknown): ConstructionTimingRequirement[] => {
  if (!Array.isArray(value)) return [];
  const kinds = ['overall_performance_period', 'phase_duration', 'milestone_deadline', 'work_access_restriction', 'notice', 'review', 'delivery', 'qualitative_dependency'];
  const units = ['calendar_days', 'workdays', 'unspecified_days', 'weeks', 'months', 'hours', 'years'];
  return value.flatMap(raw => {
    if (!raw || typeof raw !== 'object' || !kinds.includes(raw.kind) || !['required', 'planning_target', 'unspecified'].includes(raw.strength) || typeof raw.id !== 'string' || !raw.id || typeof raw.label !== 'string') return [];
    const row: ConstructionTimingRequirement = {
      id: raw.id, kind: raw.kind, strength: raw.strength, label: raw.label,
      value: typeof raw.value === 'string' ? raw.value : null,
      unit: units.includes(raw.unit) ? raw.unit : null,
      trigger: typeof raw.trigger === 'string' ? raw.trigger : null,
      phase: typeof raw.phase === 'string' ? raw.phase : null,
      start_date: typeof raw.start_date === 'string' ? raw.start_date : null,
      end_date: typeof raw.end_date === 'string' ? raw.end_date : null,
      conditions: Array.isArray(raw.conditions) ? raw.conditions.filter((entry: unknown) => typeof entry === 'string') : [],
      source_id: typeof raw.source_id === 'string' ? raw.source_id : '',
      source_locator: typeof raw.source_locator === 'string' ? raw.source_locator : '',
      evidence_quote: typeof raw.evidence_quote === 'string' ? raw.evidence_quote : '',
      discrepancy: typeof raw.discrepancy === 'string' ? raw.discrepancy : null,
      owner_override: typeof raw.owner_override?.description === 'string' && raw.owner_override.description.trim() ? { description: raw.owner_override.description } : null,
    };
    if ('value_max' in raw) row.value_max = typeof raw.value_max === 'string' ? raw.value_max : null;
    if (['uploaded_sow', 'statement_of_work', 'project_brief'].includes(raw.source_kind)) row.source_kind = raw.source_kind;
    if (typeof raw.document_name === 'string') row.document_name = raw.document_name;
    return [row];
  });
};

/** The service may retain pre-simplification fields for older drafts. Never hand them back to UI state. */
export const stripLegacyWageDeterminationFields = <T extends
  (Partial<Omit<EstimateDraft, 'construction_type'>> & {
    construction_type?: string | null;
  }) | null | undefined>(draft: T): T => {
  if (!draft || typeof draft !== 'object') return draft;
  const {
    determination_reference_date: _referenceDate,
    determination_reference_purpose: _referencePurpose,
    davis_bacon_posture: _posture,
    approximate_cost_plan_input: _privateApproximateInput,
    ...safeDraft
  } = draft as T & Record<string, unknown>;
  return { ...safeDraft, timing_requirements: normalizeTimingRequirements(safeDraft.timing_requirements), ...(safeDraft.approximate_cost_plan_settings !== undefined ? { approximate_cost_plan_settings: normalizeApproximateSettings(safeDraft.approximate_cost_plan_settings) } : {}) } as T;
};
